// src/components/clients/VerificationCheckRow.tsx
//
// Compact row renderer for a single CheckRow from buildReadout().
// Intentionally dumb — all copy + logic lives in verificationReadout.ts.
// Adding a new signal means adding a branch there, not touching this file.

import React from "react";
import { Box, Chip, Stack, Typography, alpha } from "@mui/material";
import type { CheckRow } from "./verificationReadout";
import { TONE_COLOR } from "./verificationReadout";

interface VerificationCheckRowProps {
  row: CheckRow;
}

const TONE_GLYPH: Record<CheckRow["tone"], string> = {
  pass: "✓",
  warn: "!",
  fail: "✗",
  neutral: "•",
  skipped: "–",
};

const VerificationCheckRow: React.FC<VerificationCheckRowProps> = ({ row }) => {
  const color = TONE_COLOR[row.tone];
  const isSkipped = row.tone === "skipped";

  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="flex-start"
      sx={{
        // Skipped rows drop their opacity so scannable readouts stay
        // focused on checks that actually returned data.
        opacity: isSkipped ? 0.65 : 1,
      }}
    >
      {/* Tone glyph — color-coded dot/icon that's the first thing the eye catches */}
      <Box
        sx={{
          flexShrink: 0,
          width: 22,
          height: 22,
          borderRadius: "50%",
          backgroundColor: alpha(color, 0.15),
          color,
          fontSize: 13,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mt: 0.25,
        }}
      >
        {TONE_GLYPH[row.tone]}
      </Box>

      {/* Headline + detail */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flexWrap: "wrap" }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: isSkipped ? "text.secondary" : "text.primary",
              lineHeight: 1.3,
            }}
          >
            {row.headline}
          </Typography>
          {row.value && (
            <Chip
              size="small"
              label={row.value}
              sx={{
                height: 18,
                fontSize: 10,
                fontWeight: 600,
                backgroundColor: alpha(color, 0.15),
                color,
              }}
            />
          )}
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", lineHeight: 1.4, mt: 0.25 }}
        >
          {row.detail}
        </Typography>
      </Box>
    </Stack>
  );
};

export default VerificationCheckRow;
